import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import defaultImage from "../../utils/DefaultImage";
import { IoIosArrowBack } from "react-icons/io";
import { RiArrowDownSLine } from "react-icons/ri";
import { motion, AnimatePresence } from "framer-motion";
import {
  containerVariants,
  itemVariants,
  dropdownVariants,
  buttonAnimations,
} from "../../utils/Animations";
import TableLoader from "../../utils/TableLoader";
import Banner from "../Components/Banner";
import { useTranslation } from "react-i18next";

const BeverageProduct = () => {
  // Hooks and initial state
  const navigate = useNavigate();
  const { beverage_id, rest_name } = useParams();
  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;
  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");
  const { t } = useTranslation();

  // State management
  const [userType, setUserType] = useState("");
  const [expandedItems, setExpandedItems] = useState({});
  const [beverageData, setBeverageData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [restData, setRestData] = useState([]);
  const [categories, setCategories] = useState([{ name: t('bevarageProductPage.dropDownAll') }]);
  const [selectedCategory, setSelectedCategory] = useState(t('bevarageProductPage.dropDownAll'));
  const [toggleCategory, setToggleCategory] = useState(false);

  // Update selectedCategory when language changes
  useEffect(() => {
    setSelectedCategory(t('bevarageProductPage.dropDownAll'));
  }, [t]);

  const logo = restData?.restaurant?.logo
    ? `${logoUrl}/${restData?.restaurant.logo}`
    : null;

  // Initialize user role
  useEffect(() => {
    const role = localStorage.getItem("Role");
    setUserType(role);
  }, []);

  // Memoized filtered values
  const filteredBeverageData = useMemo(() => {
    return selectedCategory === t('bevarageProductPage.dropDownAll')
      ? beverageData
      : beverageData.filter(item => item.name === selectedCategory);
  }, [selectedCategory, beverageData, t]);

  // Memoized dropdown categories
  const dropdownCategories = useMemo(() => {
    const displayedTitles = new Set(
      beverageData
        .filter(item => item?.beverages?.length)
        .map(item => item.name)
    );
    return categories.filter(
      category => category?.name === t('bevarageProductPage.dropDownAll') || displayedTitles.has(category?.name)
    );
  }, [categories, beverageData, t]);

  // Fetch beverage data
  const getBeverageData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${apiUrl}/api/v1/category-beverages/${beverage_id}`
      );
      setBeverageData(response.data || []);
    } catch (error) {
      console.error("Error fetching beverage data", error);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, beverage_id]);

  // Fetch restaurant data
  const getRestData = useCallback(async () => {
    if (!rest_name) return;

    try {
      setIsLoading(true);
      const response = await axios.get(
        `${apiUrl}/api/v1/restaurant/${rest_name}`
      );
      setRestData(response.data || []);
    } catch (error) {
      console.error("Error fetching restaurant data", error);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, rest_name]);

  // Toggle beverage description
  const toggleDescription = useCallback((index) => {
    setExpandedItems(prevState => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  }, []);

  // Initial data fetching
  useEffect(() => {
    getBeverageData();
  }, [getBeverageData]);

  useEffect(() => {
    getRestData();
  }, [getRestData]);

  // Fetch categories
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/api/v1/restaurant/${beverage_id}/category`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { pagination: 0 },
          }
        );
        setCategories([{ name: t('bevarageProductPage.dropDownAll') }, ...(response.data || [])]);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };

    fetchCategory();
  }, [apiUrl, beverage_id, token, t]);

  // Handle outside clicks for dropdown
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setToggleCategory(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  return (
    <div className="flex flex-col w-full h-[100vh] overflow-y-scroll">
      <Banner
        // desc={description}
        // restaurantId={menuData?.menu?.restaurant?.id}
        logo={logo}
      // showModalReg={showModalReg}
      // setShowModalReg={setShowModalReg}
      // restaurantName={menuData?.menu?.restaurant?.restaurant_name || ""}
      // showSentence={menuData?.menu?.show_description || 0}
      />
      <motion.div
        initial={containerVariants.initial}
        animate={containerVariants.animate}
        transition={containerVariants.transition}
        className="w-full flex items-center justify-center p-5 py-1 mt-[20px]"
      >
        <motion.div
          className="w-[90%]"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Title */}
          <motion.h1
            className="poppins-medium w-full text-center sm:text-[42px] text-[35px] sm:my-5 my-3 text-[#8B2433] uppercase"
            initial={itemVariants.initial}
            animate={itemVariants.animate}
            transition={itemVariants.transition}
          >
            {restData?.restaurant?.beverageListName || "Altre Bevande"}
          </motion.h1>

          {/* Category Dropdown */}
          <motion.div
            className="flex justify-end w-full mt-[50px]"
            variants={itemVariants}
          >
            <div className="dropdown-main flex w-[50%] justify-end items-center gap-3">
              <label className="w-full text-end text-[22px] font-semibold leading-[27px] text-[#404040] hover:text-[#8B2433] block">
                {t('bevarageProductPage.dropDownName')}
              </label>
              <motion.div className="relative w-full bg-[#FFFFFF] dropdown-container rounded-[28px]">
                <motion.button
                  className={`${toggleCategory ? "border-b-0 rounded-tl-3xl rounded-tr-3xl" : "rounded-full"} border bg-[#fff] font-medium border-[#d5d6d9] inline-flex justify-between items-center w-full px-4 py-[8px] outline-none text-[#6B6B6B] shadow-sm relative z-30 text-sm`}
                  onClick={() => setToggleCategory(!toggleCategory)}
                  {...buttonAnimations}
                >
                  <span className="mr-2 flex items-center text-[#8b2433] gap-3 poppins-medium">
                    {selectedCategory}
                  </span>
                  <RiArrowDownSLine
                    className={`transform transition-transform duration-300 ${toggleCategory ? "rotate-180" : ""}`}
                    size="24px"
                    color="#9f414f"
                  />
                </motion.button>
                <AnimatePresence>
                  {toggleCategory && (
                    <motion.div
                      className="bg-[#FFFFFF] opacity-100 z-50 border border-[#d5d6d9] absolute pt-[9px] rounded-t-none border-t-0 p-2 px-4 right-0 mt-0 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 w-full"
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                    >
                      {dropdownCategories
                        .filter((category) => category?.name !== selectedCategory)
                        .map((category, index) => (
                          <motion.label
                            key={category?.name}
                            className={`px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn ${index !== dropdownCategories.length - 1 ? "border-b border-[#8b24331f]" : ""} group hover:border-[#8b2433]`}
                            onClick={() => {
                              setSelectedCategory(category?.name);
                              setToggleCategory(false);
                            }}
                          >
                            <span className="text-xs text-[#8B8B8B] rounded-md flex items-center poppins-medium group-hover:text-[#8b2433]">
                              {category?.name}
                            </span>
                          </motion.label>
                        ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>

          {/* Beverage List */}
          <AnimatePresence>
            {isLoading ? (
              <div className="flex my-20 justify-center items-center h-full">
                <TableLoader />
              </div>
            ) : (
              <>
                {(() => {
                  const displayedTitlesSet = new Set();
                  if (!filteredBeverageData?.length) {
                    return (
                      <motion.div
                        className="text-center py-10"
                        initial={itemVariants.initial}
                        animate={itemVariants.animate}
                        exit={itemVariants.exit}
                      >
                        Nessuna bevanda trovata in questa categoria
                      </motion.div>
                    );
                  }

                  return filteredBeverageData?.map((dish, index) => {
                    if (!dish?.beverages?.length) return null;
                    const title = dish?.name;
                    return (
                      <motion.div
                        key={index}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="space-y-6"
                      >
                        {/* Render title only once per category */}
                        {!displayedTitlesSet.has(title) && (
                          <>
                            <motion.div
                              className="bg-transparent p-3 mt-5 mx-3 text-left max-w-fit poppins-medium rounded-lg"
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                            >
                              <h2 className="text-2xl text-[#8B2433] m-0">
                                {title}
                              </h2>
                            </motion.div>
                            <motion.div
                              className="h-[3px] w-full bg-[#8b2433] mx-auto my-2"
                              variants={itemVariants}
                              initial="hidden"
                              animate="visible"
                            />
                          </>
                        )}

                        {dish?.beverages?.map((beverage, bevIndex) => (
                          <motion.div
                            key={bevIndex}
                            className="relative w-[100%] rounded-3xl gap-4 md:flex-row h-fit flex lg:flex-row flex-col mt-5 mb-5 bg-[#f8f1f1] justify-center gap-x-[41px] sm:px-[50px] px-[20px] py-[37px]"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                          >
                            {beverage?.picture && (
                              <motion.div
                                className="flex justify-center"
                                variants={itemVariants}
                              >
                                <div className="min-w-[212px] min-h-[212px] flex items-center rounded-[50%] justify-center">
                                  <img
                                    className="rounded-[50%] w-[212px] h-[212px]"
                                    src={
                                      beverage.picture
                                        ? `${logoUrl}/${beverage.picture}`
                                        : defaultImage
                                    }
                                    alt={beverage.name}
                                  />
                                </div>
                              </motion.div>
                            )}
                            <motion.div
                              className="max-w-[1100px] w-[100%]"
                              variants={itemVariants}
                            >
                              <div className="pricefull half-price">
                                <ul className="flex sm:flex-row flex-col items-center mb-[8px] justify-start sm:justify-end dish-price-desktop">
                                  {beverage?.full_price && (
                                    <li
                                      className={`self-end flex items-center sm:justify-start justify-end gap-x-[10px] sm:pr-[20px] ${beverage?.pivot?.is_option_to_be_half_dish === 1 ? "sm:border-r" : ""} border-[#000000]`}
                                    >
                                      <span className="spinja-font text-[#8B2433] text-[20px] leading-[26px] font-bold block">
                                        {beverage.full_price ? beverage.full_price : "N/A"} €
                                      </span>
                                    </li>
                                  )}
                                  {beverage?.half_price && (
                                    <li className="sm:pl-[20px] flex items-center self-end justify-start gap-x-[10px]">
                                      <span className="spinja-font text-[#8B2433] text-[20px] leading-[26px] font-bold block">
                                        {beverage.half_price ? beverage.half_price : "N/A"} €
                                      </span>
                                    </li>
                                  )}
                                </ul>
                                <div>
                                  <p className="spinja-font mb-[15px] text-[21px] leading-[26px] text-[#132F40] font-bold">
                                    {beverage.name ? beverage.name : "N/A"}
                                  </p>
                                  <p className="text-[17px] mt-2 leading-[26px] text-[#6B6B6B]">
                                    {beverage.allergens?.length
                                      ? beverage.allergens.map((allergen) => allergen.name_it).join(", ")
                                      : ""}
                                  </p>
                                  {beverage.description && (
                                    <motion.div
                                      className="w-full overflow-hidden text-[#505050] text-xs poppins-light-italic"
                                      initial={{ height: expandedItems[`${index}-${bevIndex}`] ? "auto" : "2rem" }}
                                      animate={{ height: expandedItems[`${index}-${bevIndex}`] ? "auto" : "2rem" }}
                                      transition={{ duration: 0.3, ease: "easeInOut" }}
                                      variants={itemVariants}
                                    >
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: beverage.description,
                                        }}
                                      />
                                    </motion.div>
                                  )}
                                  <ul className="flex sm:flex-row flex-col items-center mb-[8px] justify-end dish-price-mobile">
                                    {beverage?.full_price && (
                                      <li
                                        className={`self-end flex items-center sm:justify-start justify-end gap-x-[10px] sm:pr-[20px] ${beverage?.pivot?.is_option_to_be_half_dish === 1 ? "sm:border-r" : ""} border-[#000000]`}
                                      >
                                        <span className="spinja-font text-[#8B2433] text-[20px] leading-[26px] font-bold block">
                                          {beverage.full_price ? beverage.full_price : "N/A"} €
                                        </span>
                                      </li>
                                    )}
                                    {beverage?.half_price && (
                                      <li className="sm:pl-[20px] flex items-center self-end justify-start gap-x-[10px]">
                                        <span className="spinja-font text-[#8B2433] text-[20px] leading-[26px] font-bold block">
                                          {beverage.half_price ? beverage.half_price : "N/A"} €
                                        </span>
                                      </li>
                                    )}
                                  </ul>
                                  <div className="pt-[41px] flex xl:flex-row items-center justify-between xl:items-end sm:items-start sm:gap-[20px]">
                                    {beverage.description &&
                                      beverage.description.length > 300 && (
                                        <motion.button
                                          className="block font-medium text-[15px] leading-[23px] text-[#8B2433] uppercase italic py-[13px] px-[30px] border border-[#8B2433] rounded-[28px] transition-all duration-300 ease-in-out"
                                          onClick={() => toggleDescription(`${index}-${bevIndex}`)}
                                          variants={buttonAnimations}
                                        >
                                          {expandedItems[`${index}-${bevIndex}`] ? "MOSTRA MENO" : "SCOPRI DI PIÙ"}
                                        </motion.button>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          </motion.div>
                        ))}
                      </motion.div>
                    );
                  });
                })()}
              </>
            )}
          </AnimatePresence>

          {/* Back Button */}
          {!isLoading && (
            <motion.div
              className="w-full flex justify-center mt-7"
              variants={itemVariants}
            >
              {["customer", "admin", "restaurant", null].includes(userType) && (
                <motion.button
                  className="flex items-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] hover:text-[#8A2533] hover:bg-[#f6efef] transition-colors group"
                  onClick={() => navigate(-1)}
                  {...buttonAnimations}
                >
                  <IoIosArrowBack
                    size={"25px"}
                    color="white"
                    className="group-hover:invert"
                  />
                  <span className="ml-2 poppins-regular">{t('landingPage.backButton')}</span>
                </motion.button>
              )}
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

BeverageProduct.propTypes = {
  isLoading: PropTypes.bool,
  beverageData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      beverages: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          full_price: PropTypes.number,
          half_price: PropTypes.number,
          picture: PropTypes.string,
          description: PropTypes.string,
          allergens: PropTypes.arrayOf(
            PropTypes.shape({
              name_it: PropTypes.string,
            })
          ),
        })
      ),
    })
  ),
};

export default BeverageProduct;